import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'

import { env } from '@/core/environments'

import enFallbackAuth from './fallback/en/auth.json'
import enFallbackCampaigns from './fallback/en/campaigns.json'
import enFallbackCommon from './fallback/en/common.json'
import enFallbackZones from './fallback/en/zones.json'
import esFallbackAuth from './fallback/es/auth.json'
import esFallbackCampaigns from './fallback/es/campaigns.json'
import esFallbackCommon from './fallback/es/common.json'
import esFallbackZones from './fallback/es/zones.json'

const useRemoteTranslations =
	env.NODE_ENV === 'production' || import.meta.env.VITE_I18N_REMOTE === 'true'

if (useRemoteTranslations) {
	// Enable HTTP backend for loading translations from a server
	i18n.use(Backend)
}

// Bundled translations for en, en-US and es / es-ES.
// These are always available immediately, so the app never hangs waiting for
// remote locale files that may return 404 (e.g. in Instagram WebView on iOS).
const bundledResources = {
	en: {
		common: enFallbackCommon,
		auth: enFallbackAuth,
		zones: enFallbackZones,
		campaigns: enFallbackCampaigns,
	},
	'en-US': {
		common: enFallbackCommon,
		auth: enFallbackAuth,
		zones: enFallbackZones,
		campaigns: enFallbackCampaigns,
	},
	es: {
		common: esFallbackCommon,
		auth: esFallbackAuth,
		zones: esFallbackZones,
		campaigns: esFallbackCampaigns,
	},
	'es-ES': {
		common: esFallbackCommon,
		auth: esFallbackAuth,
		zones: esFallbackZones,
		campaigns: esFallbackCampaigns,
	},
}

i18n
	// Detect user language
	.use(LanguageDetector)
	// Pass the i18n instance to react-i18next
	.use(initReactI18next)
	// Initialize i18next
	.init({
		// Default fallback language
		fallbackLng: 'en',
		// Debug mode - remove in production
		debug: env.NODE_ENV === 'development',

		// Always provide bundled translations so the app renders immediately
		// even when the remote API is unreachable or returns 404s.
		resources: bundledResources,

		...(useRemoteTranslations
			? {
					// Backend configuration for loading translations
					backend: {
						// URL where translations are stored
						// This could be your API endpoint or a CDN where JSON files are hosted
						loadPath: `${env.API_URL || ''}/locales/{{lng}}/{{ns}}.json`,

						// You can add custom headers if your API requires authentication
						// customHeaders: {
						//   Authorization: `Bearer ${token}`
						// },

						// How often to check for updated translations (in milliseconds)
						// Set to a higher value in production
						reloadInterval:
							env.NODE_ENV === 'development'
								? 60 * 60 * 1000
								: 60 * 60 * 24 * 1000,
					},
					// Allow bundled languages to coexist with the HTTP backend.
					// i18next will NOT attempt remote fetches for languages that already
					// have all their namespaces covered by the bundled `resources` above,
					// eliminating 404s for en / en-US / es / es-ES.
					partialBundledLanguages: true,
				}
			: {}),

		// Namespace configuration
		ns: ['common', 'auth', 'zones', 'campaigns'],
		defaultNS: 'common',

		// Don't use keys as fallback text
		keySeparator: '.',
		nsSeparator: ':',

		interpolation: {
			// React already safeguards against XSS
			escapeValue: false,
		},

		react: {
			// Wait for translations to load before rendering
			useSuspense: true,
		},
	})

export default i18n
